import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Star,
  Clock,
  Users,
  Award,
} from "lucide-react";
import colors from "../ColorConfig/colorconfig";
import GetInTouchForm from "../GetinTouchform/GetinTouch";
import Readytostart from "../Products/readytostart";
import SEO from "../../AASeo/Seo"; // Add SEO import

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const ServiceDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [serviceData, setServiceData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // FIXED: Improved parseAndCleanArray function
  const parseAndCleanArray = (data) => {
    if (Array.isArray(data)) {
      return data.filter((item) => item && item.toString().trim() !== "");
    }

    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => item && item.toString().trim() !== "");
        }
        return [];
      } catch (e) {
        // If JSON parsing fails, try to split by common delimiters
        if (data.includes(",")) {
          return data
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
        } else if (data.includes(";")) {
          return data
            .split(";")
            .map((item) => item.trim())
            .filter((item) => item !== "");
        }
        return [data.trim()].filter((item) => item !== "");
      }
    }

    return [];
  };

  // FIXED: Enhanced fetchServiceDetails function
  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("Fetching service details for ID:", id);

        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8923_Idlbay_Services_ServicesList"
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("API response data:", data);

        // Find service by ID (convert both to string for comparison)
        const service = data.find(
          (service) => service.id.toString() === id.toString()
        );
        console.log("Found service:", service);

        if (service) {
          // FIXED: Process JSON fields properly
          const processedService = {
            ...service,
            title: service.title || service.name || "Service Title",
            features: parseAndCleanArray(service.features),
            benefits: parseAndCleanArray(service.benefits),
            fullDescription:
              service.fullDescription ||
              service.description ||
              "No detailed description available",
            category: service.category || "Service",
            // FIXED: Construct full image URL properly
            image: service.media
              ? service.media.startsWith("http")
                ? service.media
                : `https://api.dremerz.net/uploads/${service.media}`
              : "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
          };

          console.log("Processed service:", processedService);
          setServiceData(processedService);
        } else {
          console.error("Service not found with ID:", id);
          setError("Service not found");
        }
      } catch (err) {
        console.error("Error fetching service details:", err);
        setError("Failed to load service details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      // FIXED: Check if data is already passed via location state
      if (location.state?.serviceData) {
        console.log(
          "Using data from location state:",
          location.state.serviceData
        );
        const processedService = {
          ...location.state.serviceData,
          // Ensure all required fields are present
          title:
            location.state.serviceData.title ||
            location.state.serviceData.name ||
            "Service Title",
          features: parseAndCleanArray(location.state.serviceData.features),
          benefits: parseAndCleanArray(location.state.serviceData.benefits),
          fullDescription:
            location.state.serviceData.fullDescription ||
            location.state.serviceData.description ||
            "No detailed description available",
          category: location.state.serviceData.category || "Service",
        };
        console.log("Processed service from state:", processedService);
        setServiceData(processedService);
        setLoading(false);
      } else {
        // Fetch from API if no data in state
        console.log("No data in state, fetching from API");
        fetchServiceDetails();
      }
    } else {
      setError("No service ID provided");
      setLoading(false);
    }
  }, [id, location.state]);

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowGetInTouch(true);
    document.body.style.overflow = "hidden";
  };

  const closeGetInTouch = () => {
    setShowGetInTouch(false);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
.service-details-header {
  background: linear-gradient(135deg, ${colors.bodyBlack} 0%, rgba(229, 118, 39, 0.1) 100%);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.service-details-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(229, 118, 39, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
  z-index: -1;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.hero-image {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.6s ease;
}

.hero-image:hover {
  transform: translateY(-10px);
  box-shadow: 0 30px 60px rgba(229, 118, 39, 0.2);
}

.hero-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
  transition: all 0.6s ease;
}

.hero-image:hover img {
  transform: scale(1.05);
}

.service-tab {
  background: transparent;
  border: 2px solid rgba(229, 118, 39, 0.3);
  color: ${colors.whiteTransparent};
  padding: 12px 24px;
  border-radius: 25px;
  margin: 0 8px 8px 0;
  transition: all 0.3s ease;
  font-weight: 500;
  cursor: pointer;
}

.service-tab:hover {
  border-color: var(--color-primary);
  color: ${colors.white};
  transform: translateY(-2px);
}

.service-tab.active {
  background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
  color: ${colors.white};
  border-color: var(--color-primary);
  box-shadow: 0 8px 20px rgba(229, 118, 39, 0.3);
}

.feature-card {
  background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
  border: 1px solid rgba(229, 118, 39, 0.2);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(229, 118, 39, 0.1), transparent);
  transition: left 0.6s ease;
}

.feature-card:hover::before {
  left: 100%;
}

.feature-card:hover {
  transform: translateX(10px);
  border-color: var(--color-primary);
  box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
}

.back-button {
  background: rgba(229, 118, 39, 0.1);
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  padding: 12px 24px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  text-decoration: none;
  font-weight: 500;
}

.back-button:hover {
  background: var(--color-primary);
  color: ${colors.white};
  transform: translateX(-5px);
}

.cta-button {
  background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
  border: none;
  color: ${colors.white};
  padding: 15px 30px;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(229, 118, 39, 0.3);
  cursor: pointer;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(229, 118, 39, 0.4);
}

.process-step {
  background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
  border: 1px solid rgba(229, 118, 39, 0.2);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  position: relative;
  transition: all 0.4s ease;
  margin-bottom: 2rem;
}

.process-step:hover {
  transform: translateY(-5px);
  border-color: var(--color-primary);
  box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
}

.process-step::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 40px;
  background: linear-gradient(to bottom, var(--color-primary), transparent);
}

.process-step:last-child::after {
  display: none;
}

.process-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-weight: bold;
  color: ${colors.white};
  font-size: 1.2rem;
}

.fade-in {
  animation: fadeIn 0.8s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.tab-content {
  animation: slideIn 0.5s ease-in-out;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}

.loading-spinner {
  border: 4px solid rgba(229, 118, 39, 0.2);
  border-radius: 50%;
  border-top: 4px solid var(--color-primary);
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 10px;
  padding: 1rem;
  text-align: center;
  color: #dc3545;
}

/* FIXED: Modal overlay styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(62, 62, 62, 0.8);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1040;
  padding: 20px;
}

.modal-content-custom {
  position: relative;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.close-button{
  position: absolute;
  top: 10px;
  right: 35px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

/* Mobile-specific styles */
@media (max-width: 768px) {
  .mobile-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  
  .back-button-mobile {
    background: rgba(229, 118, 39, 0.1);
    border: 2px solid var(--color-primary);
    color: var(--color-primary);
    padding: 8px 16px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    margin: 0;
  }
  
  .category-badge-mobile {
    background: linear-gradient(135deg, var(--color-primary)20 0%, ${colors.primaryHover}20 100%);
    color: var(--color-primary);
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 0.8rem;
    font-weight: 600;
    border: 1px solid var(--color-primary)40;
    white-space: nowrap;
  }
  
  .hero-image img {
    height: 250px;
  }
  
  .service-title-mobile {
    font-size: 2rem !important;
    margin-bottom: 1rem !important;
  }
  
  .service-description-mobile {
    font-size: 1rem !important;
    margin-bottom: 1.5rem !important;
  }
  
  .cta-button-mobile {
    padding: 12px 24px !important;
    font-size: 1rem !important;
    margin-bottom: 1.5rem !important;
    width: 100%;
    text-align: center;
  }
  
  .service-tab {
    font-size: 0.9rem;
    padding: 10px 18px;
    margin: 0 4px 8px 0;
  }
  
  .modal-content-custom {
    width: 95%;
  }
  
  .feature-card {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .service-title-mobile {
    font-size: 1.75rem !important;
    margin-top: -30px;
  }
  
  .mobile-header-row {
    margin-bottom: -20px;
  }
  
  .service-description-mobile {
    font-size: 0.95rem !important;
  }
  
  .back-button-mobile {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
  
  .category-badge-mobile {
    padding: 5px 10px;
    font-size: 0.75rem;
  }
  
  .cta-button-mobile {
    padding: 10px 20px !important;
    font-size: 0.95rem !important;
  }
}
`;

    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <div
        style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
        className="d-flex align-items-center justify-content-center"
      >
        <div className="text-center">
          <div className="loading-spinner mb-3"></div>
          <p style={{ color: colors.whiteTransparent }}>
            Loading service details...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
        className="d-flex align-items-center justify-content-center"
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="error-message">
                <h4>Service Not Found</h4>
                <p>{error}</p>
                <button
                  onClick={() => navigate("/services")}
                  className="btn btn-primary mt-3"
                  style={{
                    background: `var(--color-primary)`,
                    borderColor: colors.primary,
                    color: colors.white,
                  }}
                >
                  Back to Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Service not found
  if (!serviceData) {
    return (
      <div
        style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
        className="d-flex align-items-center justify-content-center"
      >
        <div style={{ color: colors.white }} className="text-center">
          <h3>Service not found</h3>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-primary mt-3"
            style={{
              background: `var(--color-primary)`,
              borderColor: colors.primary,
              color: colors.white,
            }}
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="tab-content">
            <div className="row">
              <div className="col-lg-12">
                <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
                  Service Overview
                </h4>
                <p
                  style={{
                    color: colors.whiteTransparent,
                    lineHeight: "1.8",
                    fontSize: "1.1rem",
                  }}
                >
                  {serviceData.fullDescription ||
                    serviceData.description ||
                    "No detailed description available"}
                </p>

                {serviceData.benefits && serviceData.benefits.length > 0 && (
                  <>
                    <h5
                      style={{
                        color: `var(--color-primary)`,
                        marginTop: "2rem",
                        marginBottom: "1rem",
                      }}
                    >
                      Key Benefits
                    </h5>
                    <div className="row">
                      {serviceData.benefits.map((benefit, index) => (
                        <div key={index} className="col-md-6 mb-3">
                          <div className="feature-card">
                            <div className="d-flex align-items-center">
                              <CheckCircle
                                style={{
                                  color: `var(--color-primary)`,
                                  marginRight: "12px",
                                }}
                                size={20}
                              />
                              <span style={{ color: colors.white }}>
                                {benefit}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case "features":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Features & Capabilities
            </h4>
            {serviceData.features && serviceData.features.length > 0 ? (
              <div className="row">
                {serviceData.features.map((feature, index) => (
                  <div key={index} className="col-lg-6 mb-3">
                    <div className="feature-card">
                      <div className="d-flex align-items-start">
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            background: `var(--color-primary)`,
                            borderRadius: "50%",
                            marginTop: "8px",
                            marginRight: "15px",
                            flexShrink: 0,
                          }}
                        ></div>
                        <div>
                          <h6
                            style={{
                              color: colors.white,
                              marginBottom: "0.5rem",
                            }}
                          >
                            {feature}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: colors.whiteTransparent }}>
                No features available for this service.
              </p>
            )}
          </div>
        );

      case "process":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Our Process
            </h4>
            <div className="row">
              <div className="col-lg-12">
                {[
                  {
                    step: 1,
                    title: "Discovery & Analysis",
                    description:
                      "We start by understanding your business requirements and analyzing your current setup to create a tailored solution.",
                  },
                  {
                    step: 2,
                    title: "Planning & Strategy",
                    description:
                      "Our team develops a comprehensive strategy and project roadmap with clear milestones and deliverables.",
                  },
                  {
                    step: 3,
                    title: "Development & Implementation",
                    description:
                      "We build and implement the solution using best practices and cutting-edge technologies.",
                  },
                  {
                    step: 4,
                    title: "Testing & Quality Assurance",
                    description:
                      "Rigorous testing ensures your solution meets all requirements and performs optimally.",
                  },
                  {
                    step: 5,
                    title: "Deployment & Support",
                    description:
                      "We deploy your solution and provide ongoing support to ensure continued success.",
                  },
                ].map((process, index) => (
                  <div
                    key={index}
                    className="process-step fade-in"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <div className="process-number">{process.step}</div>
                    <h6
                      style={{
                        color: `var(--color-primary)`,
                        marginBottom: "0.8rem",
                      }}
                    >
                      {process.title}
                    </h6>
                    <p
                      style={{
                        color: colors.whiteTransparent,
                        marginBottom: 0,
                      }}
                    >
                      {process.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}>
      <SEO
        title={
          serviceData?.title
            ? `${serviceData.title} | IDLBAY Service Details`
            : "Service Details | IDLBAY"
        }
        description={
          serviceData?.description ||
          "Explore detailed information about this service offered by IDLBAY."
        }
        keywords={`IDLBAY, service details, ${
          serviceData?.title || ""
        }, IT solutions, software, digital services`}
        url={`https://idlbay.com/servicedetails/${serviceData?.id || ""}`}
        organization={organization}
      />
      {/* Header Section */}
      <div
        className="service-details-header py-3"
        style={{ position: "relative", zIndex: 1 }}
      >
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          {/* Mobile Header Row */}
          {isMobile && (
            <div className="mobile-header-row">
              <button
                onClick={() => navigate(-1)}
                className="back-button-mobile"
                style={{
                  border: "none",
                  background: "transparent",
                  color: `var(--color-primary)`,
                  cursor: "pointer",
                }}
              >
                <ArrowLeft size={18} />
              </button>

              <span className="category-badge-mobile">
                {serviceData.category || "Service"}
              </span>
            </div>
          )}

          <div className="row align-items-center py-5">
            <div
              className="col-lg-6"
              style={{
                position: "relative",
                zIndex: 2,
                paddingBottom: "20px",
                paddingTop: "30px",
              }}
            >
              {/* Desktop Back Button and Category Badge */}
              {!isMobile && (
                <div className="mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="back-button-mobile"
                    style={{
                      border: "none",
                      background: "transparent",
                      color: `var(--color-primary)`,
                      cursor: "pointer",
                    }}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <span
                    style={{
                      background: `linear-gradient(135deg, var(--color-primary)20 0%, ${colors.primaryHover}20 100%)`,
                      color: `var(--color-primary)`,
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      border: `1px solid var(--color-primary)40`,
                      marginLeft: "1rem",
                    }}
                  >
                    {serviceData.category || "Service"}
                  </span>
                </div>
              )}

              <h1
                style={{
                  color: colors.white,
                  fontSize: isMobile ? "2rem" : "3rem",
                  fontWeight: "700",
                  marginBottom: isMobile ? "1rem" : "1.5rem",
                  lineHeight: "1.2",
                }}
                className={`fade-in ${isMobile ? "service-title-mobile" : ""}`}
              >
                {serviceData.title || serviceData.name || "Service Title"}
              </h1>

              <h2
                style={{
                  color: colors.whiteTransparent,
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  lineHeight: "1.8",
                  marginBottom: isMobile ? "1.5rem" : "2rem",
                }}
                className={`fade-in ${
                  isMobile ? "service-description-mobile" : ""
                }`}
              >
                {serviceData.description || "Service description"}
              </h2>

              <button
                onClick={handleContactClick}
                className={`cta-button fade-in ${
                  isMobile ? "cta-button-mobile" : ""
                }`}
                style={{ position: "relative", zIndex: 3 }}
              >
                Get Started Now
              </button>
            </div>

            <div
              className="col-lg-6"
              style={{ position: "relative", zIndex: 2 }}
            >
              <div className="hero-image fade-in">
                <img
                  src={serviceData.image}
                  alt={serviceData.title || serviceData.name || "Service"}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/600x400/333/fff?text=Service+Image";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <div className="d-flex flex-wrap justify-content-center mb-4">
              {[
                { key: "overview", label: "Overview" },
                { key: "features", label: "Features" },
                // { key: 'process', label: 'Process' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`service-tab ${
                    activeTab === tab.key ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">{renderTabContent()}</div>
        </div>
      </div>

      {/* FIXED: Modal with proper styling */}
      {showGetInTouch && (
        <div className="modal-overlay" onClick={closeGetInTouch}>
          <div
            className="modal-content-custom"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button" onClick={closeGetInTouch}>
              &times;
            </button>
            <GetInTouchForm isModal={true} />
          </div>
        </div>
      )}

      <Readytostart />
    </div>
  );
};

export default ServiceDetails;
